/**
 * Query keys, built in one place.
 *
 * Hand-written arrays scattered through components are how cache invalidation
 * quietly stops working - `['licences']` in one file and `['licence']` in
 * another look the same in review and are different caches.
 *
 * The nesting means a broad key invalidates everything under it:
 * `queryClient.invalidateQueries({ queryKey: queryKeys.licences.all })` clears
 * both the list and every detail.
 */
export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
    mfa: () => [...queryKeys.auth.all, 'mfa'] as const,
  },
  issues: {
    all: ['issues'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.issues.all, 'list', filters ?? {}] as const,
    detail: (id: string) => [...queryKeys.issues.all, 'detail', id] as const,
    reporters: () => [...queryKeys.issues.all, 'reporters'] as const,
  },
  licences: {
    all: ['licences'] as const,
    /** The caller's own lab licence. Takes no argument: there is only one. */
    mine: () => [...queryKeys.licences.all, 'mine'] as const,
    list: (filters?: Record<string, unknown>) =>
      [...queryKeys.licences.all, 'list', filters ?? {}] as const,
    detail: (id: string) => [...queryKeys.licences.all, 'detail', id] as const,
  },
  settings: {
    all: ['settings'] as const,
    mine: () => [...queryKeys.settings.all, 'mine'] as const,
  },
  software: {
    all: ['software'] as const,
    latest: (platform: string = 'windows') =>
      [...queryKeys.software.all, 'latest', platform] as const,
  },
} as const;
